import { Button } from '@/components/ui/button'
import { ContainerScroll } from '@/components/ui/container-scroll-animation'
import { Input } from '@/components/ui/input'
import { useSavedUrl } from '@/hooks/use-saved-url'
import { useAuth } from '@clerk/clerk-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { CornerRightUp, Sparkles } from 'lucide-react'
import { NextSeo } from 'next-seo'
import { Inter } from 'next/font/google'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

export const companies = [
  {
    name: 'Starbucks',
    designation: 'Consumer Behavior Analysis',
    image: '/images/screenshots/starbucks.png',
    industry: 'Food & Beverage',
    headquarters: 'Seattle, WA',
    website: 'https://www.starbucks.com',
    tag: 'Premier roaster and retailer of specialty coffee',
  },
  {
    name: 'Microsoft',
    designation: 'Technology Adoption Report',
    image: '/images/screenshots/microsoft.png',
    industry: 'Software',
    headquarters: 'Redmond, WA',
    website: 'https://www.microsoft.com',
    tag: 'World-leading software, services, and solutions',
  },
  {
    name: 'Apple Inc.',
    designation: 'Innovation and Development Report',
    image: '/images/screenshots/apple.png',
    industry: 'Technology',
    headquarters: 'Cupertino, CA',
    website: 'https://www.apple.com',
    tag: 'Innovator in personal electronics',
  },
  {
    name: 'IBM',
    designation: 'Enterprise Technology Trends',
    image: '/images/screenshots/ibm.png',
    industry: 'Technology',
    headquarters: 'Armonk, NY',
    website: 'https://www.ibm.com',
    tag: 'Pioneering in computing and IT consulting',
  },
  {
    name: 'Nike',
    designation: 'Brand and Product Strategy Report',
    image: '/images/screenshots/nike.png',
    industry: 'Apparel',
    headquarters: 'Beaverton, OR',
    website: 'https://www.nike.com',
    tag: 'Leading global athletic footwear and apparel',
  },
  {
    name: 'Tesla, Inc.',
    designation: 'Sustainability and Innovation Report',
    image: '/images/screenshots/tesla.png',
    industry: 'Automotive',
    headquarters: 'Palo Alto, CA',
    website: 'https://www.tesla.com',
    tag: 'Innovative electric vehicles and energy solutions',
  },
  {
    name: 'Amazon',
    designation: 'E-commerce Growth Trends',
    image: '/images/screenshots/amazon.png',
    industry: 'E-commerce',
    headquarters: 'Seattle, WA',
    website: 'https://www.amazon.com',
    tag: 'Global leader in e-commerce and cloud computing',
  },
  {
    name: 'Netflix',
    designation: 'Media Consumption Trends Report',
    image: '/images/screenshots/netflix.png',
    industry: 'Entertainment',
    headquarters: 'Los Gatos, CA',
    website: 'https://www.netflix.com',
    tag: 'Streaming entertainment service',
  },
  {
    name: 'Walmart',
    designation: 'Retail Industry Analysis',
    image: '/images/screenshots/walmart.png',
    industry: 'Retail',
    headquarters: 'Bentonville, AR',
    website: 'https://www.walmart.com',
    tag: "World's largest retailer",
  },
  {
    name: 'Adidas',
    designation: 'Global Sports Market Analysis',
    image: '/images/screenshots/adidas.png',
    industry: 'Apparel',
    headquarters: 'Herzogenaurach, Germany',
    website: 'https://www.adidas.com',
    tag: 'Leading manufacturer of sports shoes, clothing, and accessories',
  },
  {
    name: 'Coca-Cola',
    designation: 'Brand Value and Equity Report',
    image: '/images/screenshots/coca-cola.png',
    industry: 'Beverages',
    headquarters: 'Atlanta, GA',
    website: 'https://www.coca-cola.com',
    tag: 'Iconic beverage company',
  },
  {
    name: 'Typeform',
    designation: 'User Experience and Engagement Report',
    image: '/images/screenshots/typeform.png',
    industry: 'Software',
    headquarters: 'Barcelona, Spain',
    website: 'https://www.typeform.com',
    tag: 'Online form and survey builder',
  },
  {
    name: 'Figma',
    designation: 'Design Tools and Collaboration Report',
    image: '/images/screenshots/figma.png',
    industry: 'Software',
    headquarters: 'San Francisco, CA',
    website: 'https://www.figma.com',
    tag: 'Collaborative interface design tool',
  },
  {
    name: 'Slack',
    designation: 'Workplace Communication Trends',
    image: '/images/screenshots/slack.png',
    industry: 'Software',
    headquarters: 'San Francisco, CA',
    website: 'https://www.slack.com',
    tag: 'Business communication platform',
  },
  {
    name: 'Airbnb',
    designation: 'Travel and Hospitality Industry Trends',
    image: '/images/screenshots/airbnb.png',
    industry: 'Hospitality',
    headquarters: 'San Francisco, CA',
    website: 'https://www.airbnb.com',
    tag: 'Online marketplace for lodging and tourism experiences',
  },
]

const inter = Inter({ subsets: ['latin'] })

const urlSchema = z.object({
  url: z.string(),
})

type UrlForm = z.infer<typeof urlSchema>

export default function Home() {
  const router = useRouter()
  const { isSignedIn } = useAuth()

  console.log(router.query)

  const { data: url } = useSavedUrl()

  const form = useForm<UrlForm>({
    resolver: zodResolver(urlSchema),
    defaultValues: {
      url: url || '',
    },
  })

  const { register, setValue, setFocus } = form

  React.useEffect(() => {
    setFocus('url')
  }, [setFocus])

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    router.push(isSignedIn ? '/pricing' : '/auth/signin')
  }

  return (
    <>
      <NextSeo
        title="v0 report - Unleash the Power of Automated Report Generation"
        description="Upload files, suggest sections and v0 report will deliver a customizable, comprehensive company report."
      />
      <main className={`scroll-smooth flex min-h-screen  flex-col ${inter.className}`}>
        <div className="mt-60 md:mb-20 text-sm">
          <p className="mx-auto mb-2 w-9/12 md:w-4/12 text-center md:hidden block">
            Enter a url to generate a company report
          </p>
          <form
            className="shadow-lg shadow-sky-300/70 hover:shadow-sky-300/90 focus-within:shadow-sky-300/90 text-white mx-auto w-9/12 md:w-4/12 flex items-center space-x-2 px-2 py-1 mb-7 bg-zinc-900 rounded-full"
            onSubmit={handleSubmit}
          >
            <Sparkles className="text-fray-500 ml-2 w-6 h-6" />
            <Input
              type="text"
              placeholder="What company do you want a report on?"
              className="bg-zinc-900 flex-1 rounded-xl text-md border-transparent focus:border-zinc-900 focus:outline-none autofill-bg focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0"
              {...register('url', {
                onChange: (e) => {
                  localStorage.setItem('url', e.target.value)
                },
              })}
            />
            <Link
              href={isSignedIn ? '/pricing' : '/auth/signin'}
              className="flex items-center space-x-2  bg-zinc-900 hover:bg-zinc-900 text-zinc-400 hover:text-white rounded-full"
              aria-label={'Get a report'}
            >
              <CornerRightUp className="w-4 h-4 mr-2" />
            </Link>
          </form>
          <p className="select-none mx-auto w-9/12 md:w-4/12 text-center hidden md:block text-zinc-500">
            Enter a url to generate a report. eg{' '}
            <span
              className="cursor-pointer hover:underline hover:text-sky-500 hover:underline"
              onClick={() => {
                setValue('url', 'https://www.netflix.com')
                setFocus('url')
              }}
            >
              netflix.com
            </span>
          </p>
        </div>
        <div className="mt-4 flex flex-col overflow-hidden">
          <ContainerScroll
            companies={companies}
            titleComponent={
              <h1 className="text-4xl font-semibold text-black dark:text-white">
                Unleash the power of
                <br />
                <span className="text-4xl md:text-[5rem] font-bold mt-10 leading-none">
                  Automated Report Generation
                </span>
              </h1>
            }
          />
        </div>

        <div className="mx-auto mb-24">
          <h2 className="w-fit px-2 mx-auto text-center text-3xl font-bold mb-10">
            How does v0 report work?
          </h2>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 w-11/12 md:w-2/3 mx-auto mb-24">
            <div className="shadow-lg p-6 flex flex-col bg-white rounded-xl h-fit border">
              <h3 className="font-semibold mb-1">1. We gather data for you</h3>
              <p>
                We automatically collect data from multiple sources to save you time and ensure your
                report uses the latest information.
              </p>
            </div>

            <div className="shadow-lg p-6 flex flex-col bg-white rounded-xl h-fit border">
              <h3 className="font-semibold mb-1">2. You provide relevant files</h3>
              <p>
                You have the option to upload relevant documents and data to enrich reports with
                your specific insights and analysis.
              </p>
            </div>

            <div className="shadow-lg p-6 flex flex-col bg-white rounded-xl h-fit border">
              <h3 className="font-semibold mb-1">3. We write a report for you</h3>
              <p>
                Our AI-powered report generator creates a professional report with the data you
                provide and the data we gather.
              </p>
            </div>

            <div className="shadow-lg p-6 flex flex-col bg-white rounded-xl h-fit border">
              <h3 className="font-semibold mb-1">4. Refine your reports </h3>
              <p>
                Refine your reports effortlessly with a Notion style editor editor ensuring
                professional quality and impactful content.
              </p>
            </div>
          </div>
        </div>

        <section className="w-11/12 mx-auto bg-white dark:bg-gray-900 mb-48 md:w-fit drop-shadow-lg rounded-3xl border">
          <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
            <div className="mx-auto max-w-screen-sm text-center">
              <h2 className="mb-4 text-4xl tracking-tight font-bold leading-tight text-gray-900 dark:text-white">
                Get your first report
              </h2>
              <p className="mb-6 font-light text-gray-500 dark:text-gray-400 md:text-lg">
                Get instant access and start generating professional reports for your business.
              </p>
              <Button onClick={() => router.push('/auth/signup')}>Get a Report</Button>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
